import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Car, ClipboardList, RefreshCw, CheckCheck, UserCheck, ArrowLeftRight, Landmark, Truck, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import api from "@/lib/api";

const KIND_ICON = {
  saved_search: Car,
  new_request: ClipboardList,
  status: RefreshCw,
  deal_assigned: UserCheck,
  deal_reassigned: ArrowLeftRight,
  stale_deal: AlertTriangle,
  stale_escalated: AlertTriangle,
  tradein_offer: Car,
  financing_approved: Landmark,
  financing_declined: Landmark,
  delivery_confirmed: Truck,
  delivery_reschedule: Truck,
};

function playChime() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    [880, 1174.66].forEach((f, i) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "sine";
      o.frequency.value = f;
      o.connect(g); g.connect(ctx.destination);
      const t = ctx.currentTime + i * 0.14;
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.14, t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.25);
      o.start(t); o.stop(t + 0.28);
    });
    setTimeout(() => { try { ctx.close(); } catch {} }, 900);
  } catch {}
}

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

export function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const timer = useRef(null);
  const announced = useRef(new Set());
  const initialized = useRef(false);

  const poll = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      const list = data.items || [];
      setItems(list);
      setUnread(data.unread || 0);
      if (!initialized.current) {
        list.forEach((n) => announced.current.add(n.id));
        initialized.current = true;
        return;
      }
      const fresh = list.filter((n) => !n.read && n.kind === "deal_assigned" && !announced.current.has(n.id));
      list.forEach((n) => announced.current.add(n.id));
      if (fresh.length) {
        playChime();
        const n = fresh[0];
        toast(n.title, { description: n.body, action: n.link ? { label: "Open", onClick: () => navigate(n.link) } : undefined });
      }
    } catch {}
  }, [navigate]);

  useEffect(() => {
    poll();
    timer.current = setInterval(poll, 30000);
    return () => clearInterval(timer.current);
  }, [poll]);

  useEffect(() => {
    if (open) poll();
  }, [open, poll]);

  const openItem = async (n) => {
    setOpen(false);
    if (!n.read) {
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
      setUnread((u) => Math.max(0, u - 1));
      try { await api.post(`/notifications/${n.id}/read`); } catch {}
    }
    if (n.link) navigate(n.link);
  };

  const markAll = async () => {
    setItems((prev) => prev.map((x) => ({ ...x, read: true })));
    setUnread(0);
    try { await api.post("/notifications/read-all"); } catch {}
  };

  const snooze = async (n) => {
    const m = n.link && n.link.match(/\/requests\/([^?/]+)/);
    const rid = m && m[1];
    if (!rid) return;
    setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
    setUnread((u) => (n.read ? u : Math.max(0, u - 1)));
    try {
      await api.post(`/requests/${rid}/snooze-nudge`);
      if (!n.read) await api.post(`/notifications/${n.id}/read`);
      toast.success("Snoozed for a day");
    } catch { toast.error("Could not snooze"); }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="relative text-slate-300 hover:bg-slate-800 hover:text-white" data-testid="notification-bell-button" aria-label="Notifications">
          <Bell className="h-5 w-5" />
          {unread > 0 && (
            <span data-testid="notification-unread-badge" className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-rose-600 px-1 text-[10px] font-bold leading-none text-white ring-2 ring-slate-950">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 overflow-hidden rounded-md border border-slate-800 bg-slate-950 p-0 text-slate-100 shadow-2xl" data-testid="notification-panel">
        <div className="flex items-center justify-between border-b border-slate-800 px-4 py-3">
          <span className="font-display text-sm font-bold uppercase tracking-wide text-white">Notifications</span>
          {items.some((n) => !n.read) && (
            <button onClick={markAll} data-testid="notification-mark-all-read" className="flex items-center gap-1 text-xs font-semibold text-rose-400 hover:text-rose-300">
              <CheckCheck className="h-3.5 w-3.5" /> Mark all read
            </button>
          )}
        </div>
        <ScrollArea className="max-h-80">
          {items.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-slate-500 dark:text-slate-400" data-testid="notification-empty">
              You're all caught up.
            </div>
          ) : (
            <ul className="divide-y divide-slate-800/80">
              {items.map((n) => {
                const Icon = KIND_ICON[n.kind] || Bell;
                return (
                  <li key={n.id} className={n.read ? "" : "bg-rose-950/25"}>
                    <button
                      onClick={() => openItem(n)}
                      data-testid={`notification-item-${n.id}`}
                      className="flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-slate-900"
                    >
                      <span className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${n.read ? "bg-slate-800 text-slate-400 dark:text-slate-500" : "bg-rose-600/20 text-rose-400"}`}>
                        <Icon className="h-4 w-4" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-semibold text-slate-100">{n.title}</span>
                        {n.body && <span className="mt-0.5 block truncate text-xs text-slate-400 dark:text-slate-500">{n.body}</span>}
                        <span className="mt-1 block text-[11px] text-slate-500 dark:text-slate-400">{timeAgo(n.created_at)}</span>
                      </span>
                      {!n.read && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-rose-500" />}
                    </button>
                    {n.kind === "stale_deal" && (
                      <div className="flex justify-end px-4 pb-2">
                        <button
                          onClick={() => snooze(n)}
                          data-testid={`notification-snooze-${n.id}`}
                          className="rounded-md border border-slate-700 px-2 py-1 text-[11px] font-semibold text-slate-300 transition-colors hover:bg-slate-900"
                        >
                          Snooze 1 day
                        </button>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
